import { useTranslation } from 'react-i18next';
import { ErrorState, StaleBanner } from '../ui/AsyncState';
import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { useRecoveryQuery as useQuery } from '../ui/useRecoveryQuery';
import { staticQueryPolicy } from '../../api/queryPolicies';
import { getUsers } from '../../api/endpoints/admin';
import type { AdminUserResponse, UserRole } from '../../api/types';
import { partyLabel } from './partyLabel';
import { UserFormModal } from './UserFormModal';
import { prefillFromSearch } from './userPickerPrefill';
import { useOpsUser } from '../../features/ownership/opsUser';
import styles from './UserPicker.module.css';

type AddableRole = Exclude<UserRole, 'ADMIN'>;

const ADD_LABEL: Record<AddableRole, string> = {
  SHIPPER: '+ Add new shipper',
  FLEET_MANAGER: '+ Add new partner',
  DRIVER: '+ Add new driver',
};

// The add-new dialog is portalled out of the outer form; React events still bubble
// through the component tree, so keep its submit and keys away from the outer dialog.
const stop = (e: SyntheticEvent) => e.stopPropagation();

interface Props {
  role: UserRole;
  value: AdminUserResponse | null;
  onChange: (user: AdminUserResponse | null) => void;
  placeholder?: string;
}

/**
 * Searchable user select (design's chip-picker): type to search users of a
 * role, click to select; selected user shows as a removable chip.
 */
export function UserPicker({ role, value, onChange, placeholder }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const focusChip = useRef(false);
  const chipRemoveRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const wasAdding = useRef(false);
  // Creating a user is OPS_MANAGER only (R67): an executive can search and pick, not add.
  const { isManager } = useOpsUser();
  const addLabel = role === 'ADMIN' || !isManager ? null : ADD_LABEL[role];

  useEffect(() => {
    if (value && focusChip.current) chipRemoveRef.current?.focus();
    focusChip.current = false;
  }, [value]);

  // The inner dialog's autofocused field is what its DialogShell restores focus to,
  // so after Cancel/Escape put focus back on the add button (or the search input).
  useEffect(() => {
    if (adding) {
      wasAdding.current = true;
      return;
    }
    if (!wasAdding.current) return;
    wasAdding.current = false;
    if (value) return;
    (addRef.current?.isConnected ? addRef.current : inputRef.current)?.focus();
  }, [adding, value]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'users', role, 'picker', search],
    queryFn: () => getUsers({ role, search: search || undefined, size: 8 }),
    enabled: open,
    ...staticQueryPolicy,
  });

  const noMatch = !isError && !isLoading && (!data || data.items.length === 0);

  if (value) {
    const label = partyLabel({
      company: value.companyName,
      name: value.fullName,
      phone: value.phoneNumber,
      fallback: `#${value.id}`,
    });
    return (
      <div className={styles.chip}>
        <span className={styles.labels}>
          <span className={`${styles.chipName} ${styles.truncate}`} title={label.primary}>
            {label.primary}
          </span>
          {label.secondary && (
            <span className={`${styles.secondary} ${styles.truncate}`} title={label.secondary}>
              {label.secondary}
            </span>
          )}
        </span>
        <button
          ref={chipRemoveRef}
          type="button"
          className={styles.chipRemove}
          onClick={() => onChange(null)}
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <input
        ref={inputRef}
        className={styles.input}
        value={search}
        placeholder={placeholder ?? 'Search by company, name or phone…'}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
      />
      {open && (
        <div className={styles.dropdown}>
          {isError && data && <StaleBanner onRetry={() => refetch()} />}
          {isError && !data ? (<ErrorState title={t('domain.userPicker.searchError')} onRetry={() => refetch()} />) : isLoading ? (
            <div className={styles.dropdownNote}>{t('domain.userPicker.searching')}</div>
          ) : noMatch ? (
            <div className={styles.noMatch}>
              <span className={styles.noMatchText}>
                {search.trim() ? `No match for ‘${search.trim()}’ ·` : 'No matches'}
              </span>
              {addLabel && (
                <button
                  ref={addRef}
                  type="button"
                  className={styles.addNewEmphasis}
                  onClick={() => setAdding(true)}
                >
                  {addLabel}
                </button>
              )}
            </div>
          ) : (
            data?.items.map((u) => {
              const label = partyLabel({
                company: u.companyName,
                name: u.fullName,
                phone: u.phoneNumber,
                fallback: 'No name',
              });
              return (
                <button
                  key={u.id}
                  type="button"
                  className={styles.option}
                  onClick={() => {
                    onChange(u);
                    setOpen(false);
                  }}
                >
                  <span className={styles.truncate} title={label.primary}>
                    {label.primary}
                  </span>
                  {label.secondary && (
                    <span className={`${styles.secondary} ${styles.truncate}`} title={label.secondary}>
                      {label.secondary}
                    </span>
                  )}
                </button>
              );
            })
          )}
          {addLabel && !noMatch && (
            <button
              ref={addRef}
              type="button"
              className={styles.addNew}
              onClick={() => setAdding(true)}
            >
              {addLabel}
            </button>
          )}
        </div>
      )}
      {adding &&
        isManager &&
        role !== 'ADMIN' &&
        createPortal(
          <div onSubmit={stop} onKeyDown={stop}>
            <UserFormModal
              role={role}
              initialValues={prefillFromSearch(search, role)}
              onSaved={(u) => {
                focusChip.current = true;
                onChange(u);
                setOpen(false);
              }}
              onClose={() => setAdding(false)}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
